// ItemMaterialCaptureModal.jsx
// Wizard-style modal that steps through each material item for per-item material capture.
// Used when finishing a service that has material-type items (from templates).

import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import MaterialScanningSection from './MaterialScanningSection';
import { completeItemWithMaterials } from '../../services/serviceItemsService';
import InventoryAPI from '../../services/inventoryService';

class ItemMaterialCaptureModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 0,
      itemMaterials: {},   // { [stepIndex]: { materials: [], scannedData: {} } }
      saving: false,
      error: null,
      productsById: {},    // product_id -> product (for packaging + category)
      units: [],
      bundleResolved: {},  // { [stepIndex]: { lot, baseQty, sellQty, ... } } for bundled items
      prepLoading: false
    };
  }

  componentDidMount() {
    this.prepareItems();
  }

  componentDidUpdate(prevProps) {
    // Reset when items change
    if (prevProps.items !== this.props.items) {
      this.setState(
        { currentStep: 0, itemMaterials: {}, saving: false, error: null, bundleResolved: {} },
        () => this.prepareItems()
      );
    }
  }

  unitName = (unitId) => {
    const u = (this.state.units || []).find(x => String(x.unit_id) === String(unitId));
    return u ? (u.unit_code || u.unit_name || '') : '';
  };

  // The product backing a material item (actual choice wins over recommended).
  productForItem = (item) => {
    const pid = item.actual_product_id || item.recommended_product_id;
    return this.state.productsById[pid] || null;
  };

  // A bundled component is an included takeaway product (e.g. a cleaner included
  // with a Ceramic service) — handed over whole, not consumed/measured. It's
  // identified by the PERSISTED is_bundle flag (set via the catalog "Bundled"
  // toggle), NOT by category difference: an included product can share the
  // service's category. Legacy rows with no is_bundle fall back to the old
  // cross-category heuristic. Either way we need a resolvable product to auto-resolve.
  isBundleItem = (item) => {
    const p = this.productForItem(item);
    if (item.is_bundle !== undefined && item.is_bundle !== null) {
      return !!item.is_bundle && !!p;
    }
    const svcCat = (this.props.service?.service_category || '').toLowerCase();
    const prodCat = (p?.category_name || '').toLowerCase();
    return !!p && !!prodCat && !!svcCat && prodCat !== svcCat;
  };

  // Load products + units, then auto-resolve a FIFO lot for each bundled item and
  // pre-populate its materials in BASE units so handleFinish deducts without scanning.
  prepareItems = async () => {
    const { items, installerId, locationId } = this.props;
    if (!items || items.length === 0) return;
    this.setState({ prepLoading: true });
    try {
      const [stockData, unitsData] = await Promise.all([
        InventoryAPI.getStockLevels(installerId, {}, locationId).catch(() => []),
        InventoryAPI.getUnitsOfMeasure().catch(() => [])
      ]);
      const productsById = {};
      (stockData || []).forEach(p => { productsById[p.product_id || p.id] = p; });

      this.setState({ productsById, units: unitsData || [] }, async () => {
        const bundleResolved = {};
        const prePop = {};
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (!this.isBundleItem(item)) continue;
          const p = this.productForItem(item);
          const productId = p.product_id || p.id;
          const factor = parseFloat(p.base_units_per_sell_unit) || 1;
          // expected_qty is stored in BASE units; default to 1 sell unit (× factor) if absent.
          const expBase = parseFloat(item.expected_quantity != null ? item.expected_quantity : item.expected_qty);
          const baseQty = (!isNaN(expBase) && expBase > 0) ? expBase : factor;
          const sellQty = +(baseQty / factor).toFixed(4);
          const baseName = this.unitName(p.unit_id);
          const sellName = (p.sell_unit_id && this.unitName(p.sell_unit_id)) || baseName;

          // FIFO lot: oldest in-stock lot with remaining > 0
          let lot = null, inStock = false;
          try {
            const lots = await InventoryAPI.getInventoryItems(productId, installerId, 'in_stock', locationId);
            const usable = (lots || []).filter(l => (parseFloat(l.quantity_remaining) || 0) > 0)
              .sort((a, b) => {
                const ka = a.received_date || a.created_at, kb = b.received_date || b.created_at;
                if (ka && kb) return new Date(ka) - new Date(kb);
                return (a.id || 0) - (b.id || 0);
              });
            lot = usable.find(l => (parseFloat(l.quantity_remaining) || 0) >= baseQty) || usable[0] || null;
            inStock = !!lot && (parseFloat(lot.quantity_remaining) || 0) >= baseQty;
          } catch (e) { /* leave lot null → completes without deduction */ }

          bundleResolved[i] = {
            productName: p.brand ? `${p.brand} - ${p.product_name || p.name}` : (p.product_name || p.name),
            baseQty, sellQty, sellName, baseName, factor, lot, inStock
          };
          prePop[i] = {
            materials: [{
              inventory_item_id: lot ? lot.id : null,
              product_id: productId,
              quantity_used: baseQty,   // BASE units — handleFinish deducts this as-is
              isManualEntry: false
            }]
          };
        }
        this.setState(prev => ({
          bundleResolved,
          itemMaterials: { ...prev.itemMaterials, ...prePop },
          prepLoading: false
        }));
      });
    } catch (e) {
      this.setState({ prepLoading: false });
    }
  };

  // Store materials from MaterialScanningSection for a given step
  handleMaterialsChange = (stepIndex, materials) => {
    this.setState(prevState => ({
      itemMaterials: {
        ...prevState.itemMaterials,
        [stepIndex]: {
          ...prevState.itemMaterials[stepIndex],
          materials: materials || []
        }
      }
    }));
  };

  // Store scanned data (brand, product, roll) for a given step
  handleMaterialScanned = (stepIndex, scannedData) => {
    this.setState(prevState => ({
      itemMaterials: {
        ...prevState.itemMaterials,
        [stepIndex]: {
          ...prevState.itemMaterials[stepIndex],
          scannedData: scannedData || {}
        }
      }
    }));
  };

  handleNext = () => {
    const { items } = this.props;
    const { currentStep } = this.state;
    if (currentStep < items.length - 1) {
      this.setState({ currentStep: currentStep + 1 });
    }
  };

  handlePrevious = () => {
    const { currentStep } = this.state;
    if (currentStep > 0) {
      this.setState({ currentStep: currentStep - 1 });
    }
  };

  handleFinish = async () => {
    const { items, onComplete, service } = this.props;
    const { itemMaterials } = this.state;

    console.log('[ItemMaterialCaptureModal] handleFinish called');
    console.log('[ItemMaterialCaptureModal] items:', items.map(i => ({ id: i.id, label: i.label })));
    console.log('[ItemMaterialCaptureModal] itemMaterials state:', JSON.stringify(itemMaterials, null, 2));

    this.setState({ saving: true, error: null });

    try {
      // Process each item
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const stepData = itemMaterials[i];
        const materials = stepData?.materials || [];

        // Build material body for this item — only send columns that exist on request_service_tasks
        // (name/brand/barcode are returned via JOINs on GET, not stored directly)
        let materialBody = null;
        if (materials.length > 0) {
          const firstMaterial = materials[0];
          materialBody = {
            actual_product_id: firstMaterial.product_id || null,
            inventory_item_id: firstMaterial.inventory_item_id || null,
            quantity_used: parseFloat(firstMaterial.quantity_used) || null
          };

          // Deduct inventory for each scanned material on this item
          for (const mat of materials) {
            if (mat.inventory_item_id && !mat.isManualEntry) {
              const qty = parseFloat(mat.quantity_used) || 0;
              if (qty > 0) {
                try {
                  await InventoryAPI.deductInventory({
                    itemId: mat.inventory_item_id,
                    quantity: qty,
                    requestId: service?.request_id,
                    requestServiceTaskId: item.id
                  });
                } catch (err) {
                  console.error('Error deducting inventory for item:', item.label, err);
                }
              }
            }
          }
        }

        // Complete the item (with or without material data)
        try {
          await completeItemWithMaterials(item.id, materialBody);
        } catch (err) {
          console.error('Error completing item:', item.label, err);
        }
      }

      onComplete(null);
    } catch (err) {
      console.error('Error in item material capture finish:', err);
      this.setState({ saving: false, error: 'Failed to save material data. Please try again.' });
    }
  };

  // Read-only confirmation card for an auto-resolved bundled product (no scanning).
  renderBundleCard = (stepIndex) => {
    const r = this.state.bundleResolved[stepIndex];
    if (!r) {
      return (
        <div className="text-center text-muted py-3">
          <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
          Resolving included product…
        </div>
      );
    }
    const sellLabel = `${r.sellQty} ${r.sellName}${r.sellQty === 1 ? '' : 's'}`;
    return (
      <div className="p-3 border rounded">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="font-weight-bold">{r.productName}</div>
            <div className="text-muted small">Included with this package — handed to the customer.</div>
          </div>
          <div className="text-right">
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{sellLabel}</div>
            {r.factor > 1 && <div className="text-muted small">= {r.baseQty} {r.baseName}</div>}
          </div>
        </div>
        <hr className="my-2" />
        {r.inStock ? (
          <div className="text-success small">
            <i className="fa-sharp fa-regular fa-circle-check mr-1"></i>
            In stock — deducts on finish{r.lot?.lot_number ? ` (lot ${r.lot.lot_number})` : ''}.
          </div>
        ) : r.lot ? (
          <div className="text-warning small">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-1"></i>
            Low stock — will deduct what's available.
          </div>
        ) : (
          <div className="text-danger small">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-1"></i>
            No stock found — item completes without a deduction.
          </div>
        )}
      </div>
    );
  };

  render() {
    const { show, onHide, items, categoryConfig, installerId, service } = this.props;
    const { currentStep, saving, error } = this.state;

    if (!items || items.length === 0) return null;

    const currentItem = items[currentStep];
    const isLastStep = currentStep === items.length - 1;
    const isFirstStep = currentStep === 0;

    // Expected quantity: prefer item-level, fall back to service-level (from installer_services pricing)
    const itemExpectedQty = currentItem.expected_quantity || currentItem.expected_qty || null;
    const serviceExpectedQty = service?.expected_quantity || service?.expected_material_qty || null;
    const expectedQty = itemExpectedQty || serviceExpectedQty || null;

    return (
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-cube mr-2"></i>
            Material Collection &mdash; Item {currentStep + 1} of {items.length}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <div className="alert alert-danger mb-3">
              <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
          )}

          {/* Item context header */}
          <div className="mb-3 p-2 bg-light rounded">
            <div className="font-weight-bold">
              {service?.service_category ? `${service.service_category} - ` : ''}{currentItem.label}
            </div>
            {currentItem.recommended_product_name && (
              <div className="text-muted small">
                <i className="fa-sharp fa-regular fa-tag mr-1"></i>
                Recommended: {currentItem.recommended_product_brand
                  ? `${currentItem.recommended_product_brand} - ${currentItem.recommended_product_name}`
                  : currentItem.recommended_product_name}
              </div>
            )}
          </div>

          {/* Bundled product (cross-category) → auto-resolved whole-package handover.
              Otherwise the normal scan/measure flow for consumed materials. */}
          {this.isBundleItem(currentItem)
            ? this.renderBundleCard(currentStep)
            : (
              <MaterialScanningSection
                key={`item-scan-${currentStep}`}
                installerId={installerId}
                locationId={this.props.locationId}
                categoryId={categoryConfig?.category_id}
                categoryName={service?.service_category}
                defaultUnitId={categoryConfig?.default_unit_id}
                expectedQty={expectedQty}
                onMaterialsChange={(materials) => this.handleMaterialsChange(currentStep, materials)}
                onMaterialScanned={(data) => this.handleMaterialScanned(currentStep, data)}
              />
            )}

          {/* Step indicator dots */}
          {items.length > 1 && (
            <div className="text-center mt-3">
              {items.map((_, idx) => (
                <span
                  key={idx}
                  className={`d-inline-block mx-1 rounded-circle ${idx === currentStep ? 'bg-primary' : 'bg-secondary'}`}
                  style={{ width: 8, height: 8, opacity: idx === currentStep ? 1 : 0.3 }}
                />
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {/* Skip / Previous */}
          <div className="mr-auto">
            {!isFirstStep && (
              <Button variant="outline-secondary" onClick={this.handlePrevious} disabled={saving}>
                <i className="fa-sharp fa-regular fa-arrow-left mr-1"></i> Previous
              </Button>
            )}
          </div>

          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>

          {isLastStep ? (
            <Button variant="success" onClick={this.handleFinish} disabled={saving}>
              {saving ? (
                <>
                  <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-sharp fa-regular fa-check mr-2"></i>
                  Finish
                </>
              )}
            </Button>
          ) : (
            <Button variant="primary" onClick={this.handleNext} disabled={saving}>
              Next Item <i className="fa-sharp fa-regular fa-arrow-right ml-1"></i>
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ItemMaterialCaptureModal;
